'use client';

import { useState, useEffect, useCallback } from 'react';
import { slugify } from '@/lib/slug';

export interface City {
  id: string;
  name: string;
  name_zh: string;
  name_fr: string | null;
  name_es: string | null;
  name_ar: string | null;
  name_ru: string | null;
  name_pt: string | null;
  country: string;
  country_iso3: string;
  country_iso2: string;
  area_km2: number;
  area_rank: number;
  center: [number, number];
  bbox: [number, number, number, number];
  path: string;
  slug: string;
  color: string; // 用于视觉区分
}

export interface CitiesStats {
  totalCities: number;
  totalArea: number;
  largestCity: string;
  smallestCity: string;
}

// 城市颜色配置 - 基于国家分组
const CITY_COLORS = {
  'CHN': '#EF4444',      // 中国 - 红色
  'JPN': '#3B82F6',      // 日本 - 蓝色
  'GBR': '#10B981',      // 英国 - 绿色
  'USA': '#F59E0B',      // 美国 - 橙色
  'TWN': '#8B5CF6',      // 台湾 - 紫色
  'BRA': '#06B6D4',      // 巴西 - 青色
  'IND': '#84CC16',      // 印度 - 黄绿色
  'FRA': '#F97316',      // 法国 - 橙红色
  'EGY': '#14B8A6',      // 埃及 - 青绿色
  'AUS': '#EC4899',      // 澳大利亚 - 粉色
  'SGP': '#6366F1',      // 新加坡 - 靛蓝色
  'DEU': '#0EA5E9',      // 德国 - 天蓝色
  'CAN': '#22C55E',      // 加拿大 - 绿色
  'AUT': '#A855F7',      // 奥地利 - 紫色
  'ITA': '#F59E0B',      // 意大利 - 橙色
  'RUS': '#EF4444',      // 俄罗斯 - 红色
  'ARE': '#10B981',      // 阿联酋 - 绿色
  'NLD': '#3B82F6',      // 荷兰 - 蓝色
  'KOR': '#8B5CF6',      // 韩国 - 紫色
  'HKG': '#F59E0B',      // 香港 - 橙色
  'MEX': '#06B6D4',      // 墨西哥 - 青色
};

export function useCities() {
  const [cities, setCities] = useState<City[]>([]);
  const [stats, setStats] = useState<CitiesStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCities = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // 加载区域数据
        const regionsResponse = await fetch('/data/minis/regions.json');

        if (!regionsResponse.ok) {
          throw new Error('Failed to load regions data');
        }
        
        const regionsData = await regionsResponse.json();
        
        // 过滤出城市数据
        const cityData = regionsData.filter((region: any) => region.kind === 'city');
        
        // 处理城市数据
        const processedCities: City[] = cityData.map((city: any) => ({
          id: city.id,
          name: city.name,
          name_zh: city.name_zh || city.name,
          name_fr: city.name_fr || null,
          name_es: city.name_es || null,
          name_ar: city.name_ar || null,
          name_ru: city.name_ru || null,
          name_pt: city.name_pt || null,
          country: city.country || 'Unknown',
          country_iso3: city.country_iso3 || '',
          country_iso2: city.country_iso2 || '',
          area_km2: city.area_km2 || 0,
          area_rank: city.area_rank || 0,
          center: city.center || [0, 0],
          bbox: city.bbox || [0, 0, 0, 0],
          path: city.path || `data/regions/${city.id}.json`,
          slug: slugify(city.name),
          color: CITY_COLORS[city.country_iso3 as keyof typeof CITY_COLORS] || '#6B7280'
        }));

        // 按面积排序并设置排名
        processedCities.sort((a, b) => b.area_km2 - a.area_km2);
        processedCities.forEach((city, index) => {
          city.area_rank = index + 1;
        });
        
        setCities(processedCities);

        // 计算统计信息
        const totalArea = processedCities.reduce((sum, city) => sum + city.area_km2, 0);
        const largestCity = processedCities[0]?.name || '';
        const smallestCity = processedCities[processedCities.length - 1]?.name || '';

        setStats({
          totalCities: processedCities.length,
          totalArea: Math.round(totalArea),
          largestCity,
          smallestCity
        });

      } catch (err) {
        console.error('Error loading cities:', err);
        setError(err instanceof Error ? err.message : 'Failed to load cities');
      } finally {
        setLoading(false);
      }
    };

    loadCities();
  }, []); // 只在组件挂载时执行一次

  // 搜索城市
  const searchCities = useCallback((query: string) => {
    if (!query.trim()) return cities;
    
    const lowercaseQuery = query.toLowerCase();
    return cities.filter(city => 
      city.name.toLowerCase().includes(lowercaseQuery) ||
      city.name_zh.toLowerCase().includes(lowercaseQuery) ||
      city.country.toLowerCase().includes(lowercaseQuery)
    );
  }, [cities]);

  // 排序
  const sortCities = useCallback((sortBy: 'area' | 'name' | 'country', order: 'asc' | 'desc' = 'desc') => {
    const sorted = [...cities];
    
    switch (sortBy) {
      case 'area':
        sorted.sort((a, b) => order === 'desc' ? b.area_km2 - a.area_km2 : a.area_km2 - b.area_km2);
        break;
      case 'name':
        sorted.sort((a, b) => order === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name));
        break;
      case 'country':
        sorted.sort((a, b) => order === 'desc' ? b.country.localeCompare(a.country) : a.country.localeCompare(b.country));
        break;
    }
    
    return sorted;
  }, [cities]);

  return {
    cities,
    stats,
    loading,
    error,
    searchCities,
    sortCities,
    refetch: () => {
      // 重新加载数据的简单实现
      window.location.reload();
    }
  };
}
